import { ReactComponent as IconFilter } from '@/assets/svgs/adjustments-filter.svg';
import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg';
import { ReactComponent as IconDownload } from '@/assets/svgs/download-all-folder.svg';
import { ReactComponent as IconNoMessage } from '@/assets/svgs/no-message.svg';
import CheckboxButton from '@/components/CheckboxButton';
import Portal from '@/components/Portal';
import { ToastContext } from '@/context/toast/index';
import usePackages from '@/hooks/use-packages/index.js';
import usePage from '@/hooks/use-page';
import usePaginating from '@/hooks/use-paginating/index.js';
import { useLocation, useParams } from '@/lib/router-dom/index.js';
import messagesApi from "@/services/api/messages.js";
import { useContext, useEffect, useState } from 'react';
import DataRowContent from './components/package-data-element-payload-data-row';
import DownloadAllDataModal from './components/download-all-data-modal';
import useModal from "@/hooks/use-modal/index";
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index';

const PackageDataElementDetailsPage = () => {
	const { eclipId, dataElementId } = useParams();
	const { addToastMessage } = useContext(ToastContext)
	const location = useLocation();
	const currentPackage = location.currentState?.currentPackage;
	const currentDataElement = currentPackage?.dataElements?.find((dataElement) => dataElement.dataElementId === dataElementId);
	const currentFolder = location.currentState?.currentFolder

	const [selectedPayload, setSelectedPayload] = useState([]);
	const [fileSize, setFileSize] = useState("");
	const [downloadType, setDownloadType] = useState("");
	const [downloadIds, setDownloadIds] = useState([]);
	const [expandedRows, setExpandedRows] = useState({});

	const { breadcrumbsItems } = usePage({
		title: 'Package Sharing',
		breadcrumbs: [
			{
				label: '',
				link: '/'
			 },
			{
				label: 'Package Sharing',
				link: '/package'
			 },
			...(currentFolder?.name ?
				[
					{
						label: currentFolder.name,
						link: `/package/folder/${currentFolder.id}`
					}
				]
				: []),
			{
				label: currentPackage?.name,
				link: `/package/details/${currentPackage?.id}`
			},
			{
				label: currentDataElement?.elementName,
				link: ''
			 },
		  ]
	});

	const {
		getDataElementPayload,
		dataElementPayload,
		dataElementPayloadMeta
	} = usePackages()

	const payloadPageLimit = 10;
	const {
		itemsPerPage,
		currentPage,
		goToPrev,
		goToNext,
		handleCurrnetInput,
		pageLabel,
		sortConfig,
		setSortConfig,
	} = usePaginating(
		payloadPageLimit,
		dataElementPayloadMeta?.total,
		dataElementPayloadMeta?.totalPages,
	)
	const [nonExpiredPayloads, setNonExpiredPayloads] = useState([]);

	const { visible: payloadDownloadAllModalVisible, hide: hidePayloadDownloadAllModal, show: showPayloadDownloadAllModal } = useModal();

	function init() {
		if(sortConfig.key === "modified"){
			 getDataElementPayload(eclipId, dataElementId, {
				sort_by: "serialNumber",
				sort_order: 'DESC',
				limit: itemsPerPage,
				page: currentPage,
			})
		} else {
			getDataElementPayload(eclipId, dataElementId, {
				sort_by: sortConfig.key,
				sort_order: sortConfig.direction ? 'ASC' : 'DESC',
				limit: itemsPerPage,
				page: currentPage,
			})
		}
	}

	useEffect(() => {
		init()
	}, [currentPage, sortConfig.direction]);

	function getClassName(name) {
		if (!sortConfig) {
			return
		}

		if (sortConfig.key === name) {
			return sortConfig.direction === true ? "is-asc" : "is-desc"
		}

		return ""
	}

	function handleSort(key) {
		let direction = sortConfig.direction
		if (sortConfig.key === key) {
			direction = !direction
		} else {
			direction = true
		}

		setSortConfig({ key, direction })
	}

	function handleExpandRow(messageStoreId) {
		setExpandedRows((prevExpandedRows) => ({
		   ...prevExpandedRows,
		   [messageStoreId]: !prevExpandedRows[messageStoreId],
		}));
	 }

	/*=================================================/
		Select Payload
	/================================================*/
	useEffect(() => {
		if (selectedPayload.length) {
			findFileSize();
		}
		else {
			setFileSize(0)
		}
	}, [selectedPayload])


	async function findFileSize() {
		const payload = {
			ids: selectedPayload
		}

		const [err, response] = await messagesApi.checkDownloadAllFileSize("payload", payload);

		if (err) {
			return addToastMessage(
				err?.error?.message || "Unable to retrieve Config",
				"error"
			);
		}

		setFileSize(isNaN(response.totalFileSize) ? "0" : response.totalFileSize);
	}

	function handleSelectPayload(messageStoreId) {
		if(!selectedPayload.includes(messageStoreId)){
			return setSelectedPayload([...selectedPayload, messageStoreId])
		}

		if(selectedPayload.includes(messageStoreId)){
			const updatedSelection = selectedPayload.filter((item) => item !== messageStoreId)
			return setSelectedPayload(updatedSelection)
		}
	}

	function handleCheckedAllPayload() {
		const allPayloadsInPage = dataElementPayload?.map((payload) => payload.messageStoreId);
		let tempSelected = selectedPayload

		if(allPayloadsInPage.every((payload) => tempSelected.includes(payload))){
			const updatedSelection = tempSelected.filter((item) => !allPayloadsInPage.includes(item))
			return setSelectedPayload(updatedSelection)
		} else {
			const updatedSelection = [...new Set([...tempSelected, ...allPayloadsInPage])]
			return setSelectedPayload(updatedSelection)
		}
	}

	// Convert this into a custom hook
	useEffect(() => {
		const nonExpiredPayload = dataElementPayload?.filter(item => item.hasExpired === false) || [];
		setNonExpiredPayloads(nonExpiredPayload)
	}, [dataElementPayload])

	return (
		<>

			<Breadcrumbs breadcrumbItems={breadcrumbsItems}/>
			{nonExpiredPayloads?.map(item => item.messageStoreId)?.length > 0 && <div className="package-data-element-details__header">
				<div className="package-data-element-details__data-element-name" data-testid="page-title">{location.currentState?.dataElementName}</div>
				<div className="data-element-payload-table__download"
					onClick={() => {
						setDownloadType("data_element")
						setDownloadIds(nonExpiredPayloads?.map(item => item.messageStoreId))
						showPayloadDownloadAllModal()
					}}
					data-testid="dowload-all-payload"
				>
					<button className="button--rounded">Download All</button>
				</div>
			</div>}

			<div className="package-data-element-details__data-table">
				<div className="data-element-payload-table" data-testid="data-element-payload-table">
					<div className="data-element-payload-table__headers">
						<div className="data-element-payload-table__first-header">
							<div className="data-element-payload-table__action-btn">
								<div className="data-element-payload-table__filter" data-testid="data-element-payload-filter"></div>
							</div>
							<div className="data-element-payload-table__selected-item">
								{selectedPayload?.length > 0 && (
									<>
										<div className="data-element-payload-table__selected-item-text">{`${selectedPayload?.length} item(s) selected (${fileSize} MB):`}</div>
										<div className="data-element-payload-table__selected-item-download"
											onClick={() => {
												setDownloadType("payload")
												setDownloadIds(selectedPayload)
												showPayloadDownloadAllModal()
											}}
											data-testid="download-selected-paylaod"
										>
											<div className="data-element-payload-table__selected-item-download__icon"><IconDownload /></div>
											<div className="data-element-payload-table__selected-item-download__text">Download Selection</div>
										</div>
									</>
								)}
							</div>
						</div>
						<div className="data-element-payload-table__data-header">
							<div className="data-element-payload-header__checkbox">
								<CheckboxButton
									check={
										dataElementPayload?.length > 0 &&
										(dataElementPayload.every(payload => selectedPayload.includes(payload.messageStoreId))
										  ? true // All elements on the current page are in the selectedPayload list
										  : dataElementPayload.some(payload => selectedPayload.includes(payload.messageStoreId))
											? 'partial' //Some elements on the current page are in the selectedPayload list
											: false // None of the elements on the current page are in the selectedPayload list
										)
									  }
									submit={() => handleCheckedAllPayload()}
									disabled={dataElementPayload?.some(payload => payload.hasExpired)}
								/>
							</div>
							<div className={`payload-header-title__serial-number data-table__table__head__title__sort-order ${getClassName("serialNumber")}`}
								id="serialNumber"
							>
								S/N<span className="data-table__table__head__icon__sort-order" data-testid="sort-serial-no" onClick={(e) => {handleSort('serialNumber')}} onKeyDown={(e) => {handleSort('serialNumber')}} role="button"></span>
							</div>
							<div className="payload-header-title__verification">Verification</div>
							<div
								className={`payload-header-title__created-date data-table__table__head__title__sort-order ${getClassName("created")}`}
								id="created"
							>
								Created Date<span className="data-table__table__head__icon__sort-order" data-testid="sort-created" onClick={(e) => {handleSort('created')}} onKeyDown={(e) => {handleSort('created')}} role="button"></span>
							</div>
							<div className={`payload-header-title__attachment data-table__table__head__title__sort-order ${getClassName("attachment")}`}
								id="attachment"
							>
								Attachment<span className="data-table__table__head__icon__sort-order" data-testid="sort-attachment" onClick={(e) => {handleSort('attachment')}} onKeyDown={(e) => {handleSort('attachment')}} role="button"></span>
							</div>
							<div className="data-element-payload-header__arrow"></div>
						</div>
					</div>

					<div className="data-element-payload-table__divider"></div>

					<div className="data-element-payload-table__content">
						{dataElementPayload?.length > 0 ?
							dataElementPayload?.map((payload,index) => {
								return(
									< div className='data-element-payload-data-row__border' key={`${index}-${payload?.dataElementId}`} data-testid="payload-data-row">
										<DataRowContent
											dataElementPayload={payload}
											handleSelectPayload={handleSelectPayload}
											selectedPayload={selectedPayload}
											reload={init}
											isExpand={expandedRows[payload?.messageStoreId]}
											handleExpandRow={handleExpandRow}
										/>
									</div>
								)
							}) :
							<div className="data-table__empty">
								<div className="data-table__empty__icon">
									<IconNoMessage />
								</div>
							</div>
						}
					</div>

					<div className="data-element-payload-table__divider"></div>

					<div className="data-element-payload-table__footer">
						<div className='data-element-payload-footer__payload-entries'>
							<div className="data-table__table__meta">{`Showing ${pageLabel} of ${dataElementPayloadMeta?.total || 0} entries`}</div>
						</div>
						<div className='data-element-payload-footer__payload-page'>
							<div className="data-table__table__pagination">
								<input
									className="data-table__table__pagination__page"
									type="number"
									value={currentPage}
									onChange={(e) =>handleCurrnetInput(e)}
									min="1"
									data-testid="pagination-input"
								/>
								<span className="data-table__table__pagination__page__meta">
									{` of ${dataElementPayloadMeta?.totalPages || 1} pages`}
								</span>
								<button
									className="data-table__table__pagination__prev"
									onClick={() => {
										goToPrev()
										setExpandedRows({})
									}}
									disabled={currentPage === 1 || !dataElementPayloadMeta?.totalPages}
									data-testid="pagination-prev"
								>
									<IconChevronDown />
								</button>
								<button
									className="data-table__table__pagination__next"
									onClick={() => {
										goToNext()
										setExpandedRows({})
									}}
									disabled={currentPage === dataElementPayloadMeta?.totalPages || !dataElementPayloadMeta?.totalPages}
									data-testid="pagination-next"
								>
									<IconChevronDown />
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<DownloadAllDataModal
				visible={payloadDownloadAllModalVisible}
				close={hidePayloadDownloadAllModal}
				messageStoreId={downloadIds}
				dataElementId={dataElementId}
				packageActivity={downloadType}
				downloadName={currentPackage?.name}
				isMultiPayload={true}
			/>
		</>
	)
}

export default PackageDataElementDetailsPage;
